import { Box, Typography } from '@mui/material';
import { memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeChipData } from '../types';
import { getProbabilityColor } from '../utils';

export interface AttributeChipProps {
  chip: AttributeChipData;
  onClick?: (attributeId: string) => void;
  isSelected?: boolean;
}

export const AttributeChip = memo(function ({ chip, onClick, isSelected = false }: AttributeChipProps): ReactElement {
  const { t } = useTranslation();

  const handleClick = () => {
    if (chip.clickable && onClick) onClick(chip.id);
  };

  return (
    <Box
      display='flex'
      alignItems='stretch'
      justifyContent='space-between'
      width='100%'
      height='100%'
      sx={{
        backgroundColor: isSelected ? 'rgba(10, 31, 92, 0.08)' : 'white',
        cursor: chip.clickable ? 'pointer' : 'default',
        borderRadius: 'inherit',
        overflow: 'hidden',
      }}
      onClick={handleClick}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-start'
        flex={1}
        sx={{
          paddingRight: chip.probability ? 'calc(0.5rem + 5px)' : '0.5rem',
          minWidth: 0,
        }}
      >
        {/* Icon */}
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          sx={{
            marginRight: 0.5,
            flexShrink: 0,
          }}
        >
          {chip.icon}
        </Box>

        {/* Text */}
        <Typography
          variant='list_item'
          noWrap
          textAlign='left'
          color='black'
          textOverflow='ellipsis'
          sx={{
            fontSize: '0.875rem',
            flex: 1,
            minWidth: 0, // Allow text to shrink
            overflow: 'hidden',
          }}
        >
          {t(chip.name)}
          {chip.id.includes('edge') ? '' : ': '} {t(chip.value)}
        </Typography>
      </Box>

      {/* Probability indicator - takes full height on the right */}
      {chip.probability && (
        <Box
          sx={{
            width: '5px',
            backgroundColor: getProbabilityColor(chip.probability),
            borderRadius: '0px 4px 4px 0px',
            flexShrink: 0,
            alignSelf: 'stretch', // Take full height of container
          }}
        />
      )}
    </Box>
  );
});
